import {
  ActionName,
  ActionProcessMode,
  CategoryName,
  ExecutedActionP,
  IContextMenuItemRefreshRules,
  TContextKeys,
  tg,
} from '@infotech-soe/metaschema';

import { SIGN_ACTION_TYPE } from '../../../helpers/constants';
import IApiProvider, {
  TActionArgsValues,
} from '../../../infrastructure/providers/abstract/IApiProvider';
import MerpsApiProvider from '../../../infrastructure/providers/pb-adapter/MerpsApiProvider';
import CachedSchemaRepository from '../../../infrastructure/schemas/storage/CachedSchemaRepository';
import CategoryViewModel from '../../category/view-model/CategoryViewModel';
import { ClientContext } from '../../context/IContextService';
import { TCommandResult } from '../types';
import {
  ConfirmationMessageType,
  UserInteractor,
  UserInteractorType,
} from '../UserInteractor';
import { RefreshStrategy } from '../wrap/CategoryCommand';

import CategoryCommandImpl from './CategoryCommandImpl';

class RunActionCommandImpl extends CategoryCommandImpl<TActionArgsValues> {
  private readonly apiProvider: IApiProvider;
  constructor(
    protected category: CategoryName,
    protected action: ActionName,
    protected actionSchema: ExecutedActionP,
    protected categoryVM: CategoryViewModel,
    private available?: boolean,
    private refreshRules?: IContextMenuItemRefreshRules,
    private shortMessage?: string,
    private longMessage?: string,
    private icon?: string
  ) {
    super();
    this.apiProvider = new MerpsApiProvider();
  }

  public async execute(
    context: ClientContext,
    args: TActionArgsValues
  ): Promise<TCommandResult> {
    let nextActionProperty;

    for (const [objName, obj] of Object.entries(this.actionSchema.Returns)) {
      if ('action' in obj && obj.action) {
        nextActionProperty = objName;
        break;
      }
    }

    const ctx = context.toServerContext();

    if (this.actionSchema.ProcessMode !== 'All') {
      delete ctx.SelectedIds;
    } else if (!context.has('SelectedIds')) {
      ctx.SelectedIds = [context.get('Id')];
    }

    if (!nextActionProperty) {
      return this.apiProvider.actions.executeAction(this.action, ctx, args);
    }

    const result = await this.apiProvider.actions.executeAction(
      this.action,
      context.toServerContext(),
      this.prepareArgs(this.actionSchema, context, args)
    );

    return {
      actionResult: result,
      actionName: this.action,
      refreshRules: this.refreshRules,
    };
  }

  public async getInitialArgs(
    context: ClientContext
  ): Promise<TActionArgsValues> {
    const schemaRepository = new CachedSchemaRepository();
    let actionArgs: TActionArgsValues;

    const { Args } = this.actionSchema;

    const notOnlyContextOrStateArgs = Object.values(Args).some(
      arg => tg.hasCategoryFieldPath(arg) || tg.hasDomainProp(arg)
    );

    if (
      this.getProcessMode() === 'Current' &&
      Object.keys(Args).length !== 0 &&
      notOnlyContextOrStateArgs
    ) {
      actionArgs = await this.apiProvider.actions.getActionArgs(
        this.action,
        context.toServerContext()
      );
    } else {
      actionArgs = {};
    }

    if (!context.has('DisplayMode')) {
      return actionArgs;
    }

    let displayModeSchema;

    try {
      displayModeSchema = await schemaRepository.getDisplayMode(
        context.get('MasterCategory') || context.get('Category'),
        context.get('DisplayMode')
      );
    } catch {
      return actionArgs;
    }

    if (displayModeSchema.actionArgs) {
      const dsActionArgs = displayModeSchema.actionArgs;

      for (const args of Object.values(dsActionArgs)) {
        const controlNames = Object.keys(args);
        for (const arg of Object.keys(actionArgs)) {
          if (controlNames.includes(arg)) {
            const dsArg = args[arg];

            if (dsArg.kind === 'value') {
              actionArgs[arg] = dsArg.value;
            } else if (
              this.categoryVM.source.inParams &&
              this.categoryVM.source.inParams[dsArg.value]
            ) {
              actionArgs[arg] = this.categoryVM.source.inParams[dsArg.value];
            }
          }
        }
      }
    }

    return actionArgs;
  }

  public getName(): ActionName {
    return this.action;
  }

  public getUserInteractor(): UserInteractor {
    if (tg.isActionWithForm(this.actionSchema)) {
      return {
        type: UserInteractorType.DEFAULT_FORM,
        category: this.category,
        action: this.action,
      };
    } else if (
      tg.isActionWithConfirmationOptions(this.actionSchema) &&
      this.actionSchema.confirmation.hideConfirmation
    ) {
      return {
        type: UserInteractorType.NONE,
      };
    } else {
      return {
        type: UserInteractorType.CONFIRMATION,
        message: ConfirmationMessageType.GENERIC,
        shortMessage: this.shortMessage,
        longMessage: this.longMessage,
        icon: this.icon,
      };
    }
  }

  public isAvailable(context: ClientContext): boolean {
    if (!super.isAvailable(context)) {
      return false;
    }

    if (this.available !== undefined) {
      return this.available;
    }

    if (
      this.actionSchema.Type === 'Update' ||
      this.actionSchema.Type === 'Delete'
    ) {
      return tg.isSomething(context.get('Id'));
    }

    if (!this.actionSchema.Args) {
      return true;
    }

    if (this.actionSchema.Type === SIGN_ACTION_TYPE) {
      return (
        tg.isSomething(context.get('Id')) ||
        context.get('SelectedIds').some(tg.isSomething)
      );
    }

    for (const argName of Object.keys(this.actionSchema.Args)) {
      const argSchema = this.actionSchema.Args[argName] as any;

      if (argSchema.context) {
        const contextValue = context.get(argSchema.context);

        if (tg.isNothing(contextValue)) {
          return false;
        }
      }
    }

    return true;
  }

  public getRefreshStrategy(): RefreshStrategy {
    switch (this.actionSchema.RefreshMode) {
      case 'Current':
        return RefreshStrategy.CURRENT;
      case 'None':
        return RefreshStrategy.NONE;
      case 'All':
        return RefreshStrategy.ALL;
      default:
        return RefreshStrategy.ALL;
    }
  }

  public getRefreshRules(): IContextMenuItemRefreshRules | undefined {
    return this.refreshRules;
  }

  private prepareArgs(
    actionSchema: ExecutedActionP,
    context: ClientContext,
    args: TActionArgsValues
  ): TActionArgsValues {
    const result: TActionArgsValues = args;

    for (const [argName, arg] of Object.entries(actionSchema.Args)) {
      if (tg.hasContextProp(arg) && context.has(arg.context as TContextKeys)) {
        result[argName] = context.get(arg.context as TContextKeys);
      }
    }

    return result;
  }

  public getProcessMode(): ActionProcessMode {
    return this.actionSchema.ProcessMode;
  }
}

export default RunActionCommandImpl;
